import { Fragment, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import {
  UpdateUserDTO,
  getUserById,
  updateUser,
  verifyUserAttribute,
  generateVerificationCode,
} from 'src/api/users';
import { EditIcon } from 'src/assets/svg';
import { DisplayField, LoadingLoop } from 'src/components';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { User } from 'src/types/users';
import { showToast } from 'src/store/toast';
import { configuration } from 'src/configuration';

import { Button, Modal, TextInput, Tooltip } from '@medeloop/medeloop-components-library';
import { useTranslation } from 'react-i18next';

const DISABLED_FIELD_STYLES = 'cursor-not-allowed [&>div>div]:!bg-gray-800 [&>div>div]:opacity-60 [&>div>div>div>input]:cursor-not-allowed';
const DISABLED_DISPLAY_STYLES = 'bg-gray-800 opacity-60 rounded px-3 py-2 cursor-not-allowed';

interface ReadOnlyFieldWithTooltipProps {
  label: string;
  value: string;
  isEditMode?: boolean;
  fieldType?: 'email' | 'text';
}

const ReadOnlyFieldWithTooltip = ({ label, value, isEditMode = false, fieldType = 'text' }: ReadOnlyFieldWithTooltipProps) => {
  const { t } = useTranslation();

  const field = isEditMode ? (
    <div className={DISABLED_FIELD_STYLES}>
      <TextInput
        label={label}
        value={value}
        disabled
        type={fieldType}
      />
    </div>
  ) : (
    <div className={DISABLED_DISPLAY_STYLES}>
      <DisplayField label={label} value={value} />
    </div>
  );

  return (
    <Tooltip delayDuration={100}>
      <Tooltip.Trigger asChild>
        <div>{field}</div>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="top">
          <p>{t('to_change_use_sso_provider', { field: label })}</p>
          <Tooltip.Arrow />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip>
  );
};

export const ProfileInformationPage = () => {
  const token = useSelector(selectAuthToken);
  const { accessToken, userId, isSSO } = useSelector(selectAccount);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isEdit, setIsEdit] = useState(false);

  const [email, setEmail] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationAttribute, setVerificationAttribute] = useState('');

  const detailsQuery = useQuery<User>({
    queryKey: ['user', userId],
    queryFn: async () => {
      return getUserById({ token, id: userId });
    },
  });

  const editUserMutation = useMutation({
    mutationFn: (updatedUser: UpdateUserDTO) => {
      return updateUser({ token, updatedUser });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: t('success_profile_updated'),
        }),
      );
      detailsQuery.refetch();
    },
  });

  const verifyUserAttributeMutation = useMutation({
    mutationFn: ({
      attributeName,
      code,
      accessToken,
    }: {
      attributeName: string;
      code: string;
      accessToken: string;
    }) => {
      return verifyUserAttribute({ token, attributeName, code, accessToken });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: t('email_phone_verified', {
            attribute: verificationAttribute === 'email' ? t('email') : t('phone_number'),
          }),
        }),
      );
      detailsQuery.refetch();
    },
  });

  const generateVerificationCodeMutation = useMutation({
    mutationFn: ({
      accessToken,
      attributeName,
    }: {
      accessToken: string;
      attributeName: string;
    }) => {
      return generateVerificationCode({ token, accessToken, attributeName });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: t('verification_sent', {
            attribute: verificationAttribute === 'email' ? t('email') : t('phone_number'),
          }),
        }),
      );
    },
  });

  const handleEdit = (user: User) => {
    if (isEdit) {
      editUserMutation.mutate({
        userId,
        email: isSSO ? '' : email,
        familyName,
        givenName,
        phoneNumber: isSSO ? '' : phoneNumber,
      });

      if (!isSSO && email !== user.email) {
        setIsModalOpen(true);
        setVerificationAttribute('email');
      }

      if (!isSSO && phoneNumber !== user.phoneNumber) {
        setIsModalOpen(true);
        setVerificationAttribute('phone_number');
      }

      setIsEdit(false);
    } else {
      setEmail(user.email);
      setGivenName(user.givenName);
      setFamilyName(user.familyName);
      setPhoneNumber(user.phoneNumber);

      setIsEdit(true);
    }
  };

  const handleCancel = () => {
    setIsEdit(false);
  };

  const handleVerify = () => {
    verifyUserAttributeMutation.mutate({
      accessToken,
      attributeName: verificationAttribute,
      code: verificationCode,
    });
    setIsModalOpen(false);
  };

  const handleGenerateVerificationCode = () => {
    generateVerificationCodeMutation.mutate({
      accessToken,
      attributeName: verificationAttribute,
    });
  };

  return (
    <div className="flex flex-col bg-gray-900 rounded-lg p-6 mt-4 max-w-3xl">
      <div className="flex w-full">
        <div className="flex flex-col text-white">
          <span className="text-lg font-semibold mb-2">{t('basic_information')}</span>
          <span className="text-sm">{t('information_only_visible_to_you')}</span>
        </div>
      </div>

      {detailsQuery.isError && (
        <div className="flex rounded-md flex-grow items-center justify-center bg-red-100 p-4 text-red-500 font-semibold mt-4">
          {t('error_loading_profile')}
        </div>
      )}

      {editUserMutation.isError && (
        <div className="flex rounded-md flex-grow items-center justify-center bg-red-100 p-4 text-red-500 font-semibold mt-4">
          {t('error_editing_profile')}
        </div>
      )}

      {(detailsQuery.isLoading || editUserMutation.isPending) && (
        <div className="flex flex-grow items-center justify-center my-8">
          <LoadingLoop className="w-16" />
        </div>
      )}

      {!editUserMutation.isPending && detailsQuery.data && (
        <div className="flex flex-col text-sm text-white ">
          <div className="flex pt-4 gap-4 px-6">
            <DisplayField
              label={t('created_header')}
              value={dayjs(detailsQuery.data.createdAt).format(configuration.dateFormat)}
            />
            <DisplayField
              label={t('last_updated')}
              value={dayjs(detailsQuery.data.updatedAt).format(configuration.dateFormat)}
            />
            <DisplayField label={t('user_roles')} value={detailsQuery.data.groups[0]} />
          </div>
          <div className="flex my-4 bg-gray-950 rounded-md p-6 flex-wrap gap-4">
            {isEdit ? (
              <Fragment>
                <div className="flex w-72">
                  <TextInput
                    label={t('first_name_header')}
                    value={givenName}
                    onChange={(e) => setGivenName(e.target.value)}
                  />
                </div>
                <div className="flex w-72">
                  <TextInput
                    label={t('last_name_header')}
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                  />
                </div>
                {isSSO ? (
                  <>
                    <div className="flex w-72">
                    <ReadOnlyFieldWithTooltip
                      label={t('email_header')}
                      value={email}
                      isEditMode
                      fieldType="email"
                    />
                    </div>
                    {detailsQuery.data.phoneNumber && (
                      <div className="flex w-72">
                        <ReadOnlyFieldWithTooltip
                          label={t('phone_number_header')}
                          value={phoneNumber}
                          isEditMode
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex w-72">
                      <TextInput
                        label={t('phone_number_header')}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                      />
                    </div>
                    <div className="flex w-72">
                      <TextInput
                        label={t('email_header')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                      />
                    </div>
                  </>
                )}
              </Fragment>
            ) : (
              <Fragment>
                <DisplayField label={t('first_name_header')} value={detailsQuery.data.givenName} />
                <DisplayField label={t('last_name_header')} value={detailsQuery.data.familyName} />
                {isSSO ? (
                  <>
                    <ReadOnlyFieldWithTooltip
                      label={t('email_header')}
                      value={detailsQuery.data.email}
                    />
                    {detailsQuery.data.phoneNumber && (
                      <ReadOnlyFieldWithTooltip
                        label={t('phone_number_header')}
                        value={detailsQuery.data.phoneNumber}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <DisplayField
                      label={t('phone_number_header')}
                      value={detailsQuery.data.phoneNumber}
                    />
                    <DisplayField label={t('email_header')} value={detailsQuery.data.email} />
                  </>
                )}
              </Fragment>
            )}
          </div>
          <div className="flex w-full justify-end gap-4">
            {isEdit && (
              <Button onClick={handleCancel} variant="secondary">
                {t('cancel')}
              </Button>
            )}
            <Button onClick={() => handleEdit(detailsQuery.data)} variant="primary">
              <div className="flex items-center">
                <EditIcon className="mr-2" />
                <span>{t('edit')}</span>
              </div>
            </Button>
          </div>
          <Modal
            title={t('verify_your', {
              attribute: verificationAttribute === 'email' ? t('email') : t('phone_number'),
            })}
            isOpen={isModalOpen}
            actionType="primary"
            onCancel={() => setIsModalOpen(false)}
            actionText={t('verify_header')}
            cancelText={t('cancel')}
            actionFunction={handleVerify}
          >
            <TextInput
              label={t('please_enter_verification_sent', {
                attribute: verificationAttribute === 'email' ? t('email') : t('phone_number'),
              })}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder={t('enter_verification_code')}
            />
            <span
              className="text-sm text-cyan-300 text-border-300 cursor-pointer"
              onClick={handleGenerateVerificationCode}
            >
              {t('resend_verification_code')}
            </span>
          </Modal>
        </div>
      )}
    </div>
  );
};
